// Pure helper for the commission worksheet's cost breakdown.
//
// Split out of CommissionPages.tsx so it can be unit-tested without
// pulling in React or api.ts (same pattern as admin-team-rows.ts).
//
// It decides exactly which cost rows the worksheet renders, which is a
// company-scoped question: Mortgage shows six fixed lines whether or not
// they were charged (Baker's paper document has all six), while every
// other company shows only cost rows that actually exist — the
// commission_transaction_costs cost_type enum is mortgage-specific, so
// listing "Credit Report / Appraisal / Reinspection" on an Insurance
// worksheet asserts deductions that cannot exist (Baker, 2026-07-31).

export interface WorksheetCostLike {
  id: string;
  cost_type: string;
  description?: string | null;
  amount_cents: number;
}

export interface WorksheetTotalsLike {
  gross_commission_cents: number;
  net_income_cents: number;
}

export interface WorksheetStandardRow {
  cost_type: string;
  amount_cents: number;
  /** False when this company shows the row but no cost row exists ("$-"). */
  present: boolean;
}

export interface WorksheetBreakdown<TCost extends WorksheetCostLike> {
  standard_rows: WorksheetStandardRow[];
  extra_rows: TCost[];
  /** Sum of every cost row that exists. */
  itemized_cents: number;
  /** (gross − net) − itemized. Non-zero means the rows don't reconcile. */
  unitemized_cents: number;
  show_costs_block: boolean;
}

export function buildWorksheetBreakdown<TCost extends WorksheetCostLike>(
  vocab: { standard_cost_types: readonly string[] },
  disb: WorksheetTotalsLike,
  costs: TCost[],
): WorksheetBreakdown<TCost> {
  const standardTypes = vocab.standard_cost_types;
  const standard_rows: WorksheetStandardRow[] = standardTypes.map((cost_type) => {
    // Sum rather than find-first: two rows of the same cost_type both
    // count toward NET, so showing only the first would under-report the
    // deduction while the total stayed right.
    const matches = costs.filter((c) => c.cost_type === cost_type);
    return {
      cost_type,
      amount_cents: matches.reduce((s, c) => s + c.amount_cents, 0),
      present: matches.length > 0,
    };
  });
  const extra_rows = costs.filter((c) => !standardTypes.includes(c.cost_type));

  const itemized_cents = costs.reduce((s, c) => s + c.amount_cents, 0);
  const unitemized_cents =
    disb.gross_commission_cents - disb.net_income_cents - itemized_cents;

  return {
    standard_rows,
    extra_rows,
    itemized_cents,
    unitemized_cents,
    // Hidden entirely when a company has no standard rows, no real costs,
    // and nothing unreconciled — an empty "Minus Stewardship Transaction
    // Costs" heading on an Insurance pool is noise.
    show_costs_block:
      standard_rows.length > 0 || extra_rows.length > 0 || unitemized_cents !== 0,
  };
}
